import React from "react";

import SearchIcon from "@uui/SearchMd";

import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  ChipProps,
  IconButton,
  Input,
  SxProps,
  Typography,
} from "@mui/material";
import styled from "@mui/material/styles/styled";

import { pluralize } from "@app/common/utils";
import { lightOrDark } from "@app/embedded/theme/utils";

import LoadSpinner from "@rui/LoadSpinner";
import Stack, { Row } from "@rui/Stack";
import { roundedOutline } from "@rui/styles";

import Paginator from "./Paginator";

interface SearchBoxProps {
  autoFocus?: boolean;
  onBlur?: () => void;
  onChange: (value: string) => void;
  onClear: () => void;
  onEnter?: () => void;
  numResults?: number;
  pagination?: {
    currentPage: number;
    goToNextPage: () => void;
    goToPreviousPage: () => void;
    numPages: number;
  };
  readOnly?: boolean;
  placeholder: string;
  showPagination?: boolean;
  sx?: SxProps;
  inputSx?: SxProps;
  value?: string;
  chips?: ChipProps[];
  isSearching?: boolean;
}

const ClearSearchButton = ({ onClick }: { onClick: () => void }) => (
  <IconButton sx={{ width: "24px", height: "24px", p: 0 }} onClick={onClick}>
    <CloseIcon sx={{ fontSize: "18px", color: "shades.neutral.500" }} />
  </IconButton>
);

const IconShim = styled(Stack)({
  width: "10px",
});

const StyledWrapper = styled(Row)(({ theme }) => ({
  position: "relative",
  minWidth: "250px",
  backgroundColor: theme.palette.background.default,
  borderRadius: "4px",
  border: "1px solid",
  borderColor: lightOrDark(
    theme.palette.shades.neutral[100],
    theme.palette.transparent.white[8]
  )(theme),
  padding: "12px 8px 12px 16px",
  "&:focus-within": {
    backgroundColor: theme.palette.background.default,
    ...roundedOutline({
      borderRadius: "4px",
      border: "2px solid #7B61FF",
    }),
  },
}));

const VerticalDivider = styled(Box)(({ theme }) => ({
  borderLeft: `1px solid ${theme.palette.shades.neutral[100]}`,
  margin: "0 8px",
  width: "1px",
  height: "18px",
}));

const SearchBox = ({
  autoFocus,
  numResults,
  onBlur,
  onChange,
  onClear,
  onEnter,
  pagination,
  placeholder,
  readOnly = false,
  showPagination = false,
  sx,
  inputSx,
  value,
  chips,
  isSearching = false,
}: SearchBoxProps) => {
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.value);
  };

  const handlePageChange = (direction: number) => {
    if (!pagination) return;

    if (direction < 0) {
      pagination.goToPreviousPage();
    } else {
      pagination.goToNextPage();
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    switch (event.key) {
      case "Enter":
        event.preventDefault();
        onEnter && onEnter();
        break;
      case "Escape":
        onClear();
        break;
      default:
        break;
    }
  };

  return (
    <StyledWrapper sx={sx} variant="apart">
      <Input
        autoFocus={autoFocus}
        disableUnderline
        placeholder={placeholder}
        onBlur={onBlur}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        value={value}
        readOnly={readOnly}
        startAdornment={
          readOnly ? (
            <IconShim />
          ) : (
            <>
              {isSearching ? (
                <LoadSpinner size={18} sx={{ mr: 1 }} />
              ) : (
                <SearchIcon
                  sx={{ mr: 1, color: "shades.neutral.400", fontSize: "18px" }}
                />
              )}
              {chips}
            </>
          )
        }
        sx={{
          flexGrow: 1,
          fontSize: "1rem",
          "&::placeholder": {
            color: (theme) =>
              lightOrDark("shades.neutral.400", "shades.neutral.100")(theme),
          },
          ...inputSx,
        }}
      />
      {showPagination && pagination && (
        <>
          <VerticalDivider />
          <Paginator
            currentPage={pagination.currentPage}
            totalPages={pagination.numPages}
            onPageChange={handlePageChange}
          />
        </>
      )}
      {!pagination && value && numResults != null && (
        <>
          <VerticalDivider />
          <Typography
            variant="bodySecondaryReg"
            sx={{ color: "shades.neutral.700", pl: 1, whiteSpace: "nowrap" }}
          >
            {numResults} {pluralize("Result", numResults)}
          </Typography>
        </>
      )}
      {value && (
        <>
          <VerticalDivider />
          <ClearSearchButton onClick={() => onClear()} />
        </>
      )}
    </StyledWrapper>
  );
};

export default SearchBox;
